import { UIIcon } from '../config/icons';
import { ABFilterBar } from '../lib/ab/filter-bar';
import { ABProgressView } from '../lib/ab/widgets';
import { ABObjectCount } from '../lib/ab/widgets-admin';
import { TPdfFailure, tPdfFailureDelete, tPdfFailureGet, tPdfFailuresGetAll, tPdfFailureTest, TPdfRecognitionType, tPdfRecognize, TPdfRecognizeResponse, TPdfSpec, tPdfSpecCreate, tPdfSpecDelete, TPdfSpecField, tPdfSpecsGetAll } from '../lib/mdl/t-pdf';
import { TSeat } from '../lib/mdl/t-ticket';
import { xTaskWait } from '../lib/mdl/x-task';
import { makeActionButton, UIButton } from '../lib/ui/component/button';
import { UIDialog } from '../lib/ui/component/dialog';
import { UITextInput } from '../lib/ui/component/input';
import { UILabeled } from '../lib/ui/component/labeled';
import { UISelect } from '../lib/ui/component/select';
import { UITable, UITableDefinition } from '../lib/ui/component/table';
import { uiToast } from '../lib/ui/component/toast';
import { UICheckbox, UICheckboxRadioOptions, UIExclusiveToggles, UIRadio } from '../lib/ui/component/toggle';
import { addTooltip } from '../lib/ui/component/tooltip';
import { showWarning } from '../lib/ui/component/warning';
import { UIContent, UIDropDelegate, uiInitDropTarget } from '../lib/ui/dnd';
import { $el } from '../lib/ui/elements';
import { $fmt } from '../lib/ui/fmt';
import { uiMakeFakeUploadInput } from '../lib/ui/functions';
import { UIScreen } from '../lib/ui/screen';
import { UIRelPos, UIValue } from '../lib/ui/types';
import { UIView, UIViewSpec } from '../lib/ui/view';
import { YArrayDataSource } from '../lib/y/data-source';
import { $log } from '../lib/y/debug';
import { $on } from '../lib/y/events';
import { $fn } from '../lib/y/fn';
import { $form } from '../lib/y/form';
import { $location } from '../lib/y/location';
import { yClamp } from '../lib/y/math';
import { $time } from '../lib/y/time';
import { YId } from '../lib/y/types';

const capture_values: UIValue<TPdfSpecField>[] = [{ key: 'event_name', name: 'Event' }, { key: 'session', name: 'Session' }, { key: 'row', name: 'Row' }, { key: 'seat', name: 'Seat' }, { key: 'section', name: 'Section' }, { key: 'entrance', name: 'Entrance' }]

class CreateSpecDialog extends UIDialog {
    constructor(seats: TSeat[], title: string, spec: any) {
        super({ close: true });

        const name = new UITextInput({ required: true });

        this.content.ui_add($el.DIV('w-block l-form m-center', [
            $el.H2('title', 'Create spec'),
            new UILabeled(name, { above: 'Spec name' }),
            $el.B(title),
            $el.DIV('tl-frame-table-wrapper', $el.TABLE(['Section', 'Row', 'Seat', 'Entrance'], seats.map(k => [k.section, k.row + '', k.seat + '', k.entrance]), 'tl-frame-table')),
            $el.DIV('l-button-row', [
                new UIButton({ title: "Cancel", classes: 'm-round m-compact m-hollow', on_click: () => { this.dismiss(null) } }),
                new UIButton({
                    title: "Create spec", classes: 'm-round m-compact', on_click: () => {
                        if(!$form.validate(name)) return;
                        spec.name = name.value;
                        tPdfSpecCreate(spec).then(_r => {
                            this.dismiss(true);
                        });
                    }
                })
            ])
        ]));
    }
}

class FailureScreen extends UIScreen {
    constructor(private _failure: TPdfFailure) {
        super();
        this.title = _failure.name;
    }

    protected override fillContent(content: UIViewSpec): void {
        const failure = this._failure;
        const text_decoder = new TextDecoder('utf-8', { fatal: false });
        const pages = failure.pages.map(p => text_decoder.decode(Uint8Array.fromBase64(p)))

        const regex = new UITextInput({ required: true, lines: 5 });
        const s_flag = new UICheckbox({ title: 'Dot matches newline' });

        const captured: HTMLDivElement[] = [];
        const captures: UISelect<TPdfSpecField>[] = [];
        let captured_samples: string[][] | undefined;

        const error_message = $el.DIV();

        for(let i = 0; i < 6; i++) {
            const capture = new UISelect(capture_values);
            captures.push(capture);
            const info = $el.DIV('r-captured t-ellipsize', '\u00a0');
            addTooltip(() => {
                if(!captured_samples || !captured_samples[i])
                    return undefined;
                return captured_samples[i].join(', ').substring(0, 500);
            }, info, { position: UIRelPos.Below });
            captured.push(info);
            $on(capture, 'value', v => {
                if(v) {
                    for(let j = 0; j < 6; j++) {
                        if(i != j && captures[j].value == v)
                            captures[j].value = undefined;
                    }
                }
            })
        }

        const setError = (e: string) => {
            error_message.textContent = e;
            captured_samples = undefined;
            for(let i = 0; i < 6; i++) {
                captured[i].textContent = '\u00a0';
            }
            action_button.disabled = true;
        }

        const setWarning = (e: string) => {
            error_message.textContent = e;
            action_button.disabled = true;
        }

        const setSuccess = () => {
            error_message.textContent = '';
            action_button.disabled = false;
        }

        const checkRegex = $fn.throttled((v: string) => {
            try {
                const re = new RegExp(v, s_flag.value ? 's' : undefined);
                const matches: string[][] = [];
                for(let i = 0; i < pages.length; i++) {
                    const m = pages[i].match(re);
                    if(!m)
                        return setError(i == 0 ? 'No match' : `No match in page ${i + 1}`);
                    if(i != 0 && m.length != matches[0].length)
                        return setError(`Different number of matches in page ${i + 1}`);

                    matches.push(Array.from(m));
                }

                error_message.textContent = '';
                captured_samples = [];
                for(let i = 1; i < matches[0].length; i++) {
                    captured_samples.push(matches.map(m => m[i]).filter($fn.UNIQUE_FILTER));
                }

                for(let i = 0; i < 6; i++) {
                    captured[i].textContent = i < captured_samples.length ? captured_samples[i][0] : '\u00a0';
                }

                if(captured_samples.length >= 5) {
                    setSuccess();
                } else {
                    setWarning("Must have at least 5 capture groups");
                }
            } catch {
                setError('Invalid regular expression');
            }
        }, 1)

        $on(regex, 'value', checkRegex);
        $on(s_flag, 'value', () => { checkRegex(regex.value) });

        const action_button = makeActionButton(UIIcon.THUMBS_UP, 'Fix', () => {
            if(!captured_samples) return;
            let re = regex.value;
            if(s_flag.value)
                re = '(?s)' + re;
            const mapping: { [key in TPdfSpecField]?: number } = {};
            const value: any = { regex: re, mapping };
            for(let i = 0; i < captured_samples.length; i++) {
                const v = captures[i].value;
                if(v == undefined) {
                    captures[i].markInvalid(true, true);
                    return;
                }

                mapping[v] = i + 1;
            }

            if(!mapping.event_name || !mapping.session || !mapping.seat || !mapping.row) {
                uiToast('Not all needed captures are set');
                return;
            }

            tPdfFailureTest(failure.id, value).then(r => {
                new CreateSpecDialog(r.seats, r.title, value).present().then(() => {
                    $location.pop();
                    $location.reload();
                });
            }, uiToast)
        });
        action_button.disabled = true;

        const page_display = $el.DIV('tl-pdf-page');
        const page_number = $el.SPAN();
        let current_page: number;

        const showPage = (p: number) => {
            p = yClamp(p, 1, pages.length);
            if(p != current_page) {
                current_page = p;
                page_display.textContent = pages[p - 1];
                page_number.textContent = `Page ${current_page} of ${pages.length}`;
            }
        }

        showPage(1);

        const previous_page = new UIButton({ icon: UIIcon.ANGLE_LEFT, kind: 'k-square', classes: 'm-round', on_click: () => { showPage(current_page - 1) } });
        const next_page = new UIButton({ icon: UIIcon.ANGLE_RIGHT, kind: 'k-square', classes: 'm-round', on_click: () => { showPage(current_page + 1) } });

        content.ui_add([
            $el.FORM('ui-section w-form l-form', [
                s_flag,
                new UILabeled(regex, { above: 'Regex' }),
                error_message,
                $el.DIV('tl-pdf-captures', [captured, captures]),
                page_display,
                $el.DIV('s-tiny'),
                $el.DIV('l-input-row m-center', [previous_page, page_number, next_page])
            ]),
            action_button
        ]);
    }
}

export class AdminPdfFailuresScreen extends UIScreen {
    protected override async fillContent(content: UIViewSpec) {
        const all_failures = await tPdfFailuresGetAll();

        const count = new ABObjectCount('failures');
        count.number = all_failures.length;

        const filter_bar = new ABFilterBar({
            right_content: [count],
            no_header: true
        });

        const def = new UITableDefinition<TPdfFailure>();

        def.addValue(f => f.name, { title: 'Name', width: 200 });
        def.addValue(f => $time(f.created_at), $fmt.DATE('yyyy-MM-dd HH:mm'), { title: 'Failed at', width: -140 });
        def.addValue(f => f.is_fixed, $fmt.CHECKMARK, { title: 'Fixed', width: -80 });

        def.addButtons([
            {
                icon: UIIcon.TRASH_CAN, tooltip: 'Delete', action: async item => {
                    $log('app', 'deleting');
                    if(!item.is_fixed) {
                        $log('app', '%cnot fixed, so awaiting', $log.BLUE);
                        await showWarning({ texts: ["This file hasn't been fixed", "Are you sure you want to delete it?"] });
                    }
                    $log('app', 'now doing it');
                    await tPdfFailureDelete(item.id);
                    $log('app', 'done');
                    source.remove(item);
                }
            },
            {
                icon: UIIcon.PENCIL, tooltip: 'Edit', when: f => !f.is_fixed, action: item => {
                    tPdfFailureGet(item.id).then(f => { $location.push({ transient: new FailureScreen(f) }) });
                }
            }
        ], { width: -94 });

        def.preference_prefix = 'pdf_failures';

        const source = new YArrayDataSource(all_failures);

        content.ui_add([
            $el.SECTION('pad-t-narrow', $el.DIV('tl-new-list-block m-narrow', [
                filter_bar,
                new UITable(def, source),
            ])),
        ]);
    }
}

export class AdminPdfSpecsScreen extends UIScreen {
    protected override async fillContent(content: UIViewSpec) {
        const all_specs = await tPdfSpecsGetAll();

        const count = new ABObjectCount('specs');
        count.number = all_specs.length;

        const filter_bar = new ABFilterBar({
            right_content: [count],
            no_header: true
        });

        const def = new UITableDefinition<TPdfSpec>();

        def.addValue(f => f.name, { title: 'Name', width: 120 });
        def.addValue(f => f.regex, { title: 'Regex', width: 200, classes: 't-ellipsize' });
        def.addValue(f => f.used_count, $fmt.NUMBER, { title: 'Used', width: -70 });
        def.addValue(f => f.id, { title: 'Id', width: -280 });

        def.addButtons([
            {
                icon: UIIcon.TRASH_CAN, tooltip: 'Delete', action: item => {
                    return tPdfSpecDelete(item.id).then(() => {
                        source.remove(item);
                    });
                }
            }
            // { icon: UIIcon.PENCIL, tooltip: 'Edit', when: f => !f.fixed, action: item => {
            //     $tl.getPDFFailure(item.id).then(f => { $location.push({ transient: new FailureScreen(f) }) });
            // }}
        ], { width: -32 });

        def.preference_prefix = 'pdf_specs';

        const source = new YArrayDataSource(all_specs);

        content.ui_add([
            $el.SECTION('pad-t-narrow', $el.DIV('tl-new-list-block m-wide', [
                filter_bar,
                new UITable(def, source),
            ])),
        ]);
    }
}

class ToolResultDialog extends UIDialog {
    constructor(seats: TSeat[], spec?: TPdfSpec) {
        super({ close: true });

        this.content.ui_add($el.DIV('w-block l-form m-center', [
            spec && [
                $el.P('info', 'Spec: ' + spec.name + ' (' + spec.id + ')'),
                $el.DIV('s-tiny'),
            ],
            $el.DIV('tl-frame-table-wrapper', $el.TABLE(['Section', 'Row', 'Seat', 'Entrance'], seats.map(k => [k.section, k.row + '', k.seat + '', k.entrance]), 'tl-frame-table')),
        ]));
    }
}

export class AdminPdfButton extends UIView implements UIDropDelegate {
    private _text: HTMLElement;
    private _got_file?: boolean;
    on_response?: (response: TPdfRecognizeResponse) => void;
    private _method: TPdfRecognitionType;
    private _assign: boolean;

    constructor(method: TPdfRecognitionType = 'regex', private _session_id: YId | undefined = undefined, assign: boolean = false) {
        super('div', 'tl-pdf-button');
        uiInitDropTarget(this);
        this._method = method;
        this._assign = assign;

        this.ui_add(this._text = $el.B(['Drag a PDF file', $el.BR(), 'or', $el.BR(), 'click to upload']));

        const upload_input = uiMakeFakeUploadInput(files => {
            this._upload(files[0]);
        }, ['application/pdf'])

        this.root.addEventListener('click', () => {
            if(!this._got_file) {
                upload_input.click();
            }
        });
    }

    private _upload(file: File) {
        this._got_file = true;
        this._text.remove();
        const progress = new ABProgressView;
        this.ui_add(progress);
        progress.text = 'Uploading';

        $log('app', 'uploading', file.name)

        tPdfRecognize(file, this._method, this._session_id, this._assign, p => { progress.progress = p }).then(r => {
            progress.text = 'Processing';
            progress.progress = 0;
            return xTaskWait<TPdfRecognizeResponse>(r.task, (p => { progress.task_progress = p })).then(r => {
                progress.complete("Done!");
                this.on_response?.(r);
            }, r => {
                progress.complete("Failed");
                showWarning({ text: r, cancel_text: null });
            });
        }, uiToast);
    }

    dropEnter() {
        return !this._got_file;
    }

    drop(content: UIContent) {
        if(this._got_file) return;

        if(content.type === 'files') {
            const files = content.value as File[];
            this._upload(files[0])
        }
    }

    set method(m: TPdfRecognitionType) {
        this._method = m;
    }

    set session(s: YId | undefined) {
        this._session_id = s;
    }

    set assign(a: boolean) {
        this._assign = a;
    }
}

export class AdminPdfToolScreen extends UIScreen {
    protected override fillContent(content: UIViewSpec): void {
        const button = new AdminPdfButton;
        button.on_response = r => {
            new ToolResultDialog(r.seats, r.spec).present();
        }
        const option_values: UICheckboxRadioOptions<TPdfRecognitionType>[] = [{ key: 'regex', title: 'Regex' }, { key: 'gemini', title: 'Gemini 🧪' }, { key: 'unnumbered', title: 'Unnumbered ☠️' }];

        const options = new UIExclusiveToggles(option_values, o => new UIRadio(o));
        options.value = 'regex';
        $on(options, 'value', v => {
            button.method = v!;
        });

        content.ui_add($el.DIV('ui-section w-block l-form m-center', [
            button,
            options
        ]));
    }
}
